import { ContextMenuOption } from '../../../models/context-menu-option';
import { Custodian } from '../../../models/custodian';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { DynamicFolder } from '../../../models/dynamic-folder';
import { Endpoint, EntityType } from '../../../models/enums';
import { NavigationInfo } from '../../../models/navigation-info';
import { View } from '../../../models/view';
import { WorkingSet } from '../../../models/working-set';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { ObjectActionListType, ActionType, PermissionSet } from '../../../models/permission';
import { MediaID } from '../../../models/mediaid';
import { Batch } from '../../../models/batch';

export const ProjectDataMenu = new GenericTreeNode({
    name: 'Project Data',
    entityType: EntityType.projectData,
    automationId: 'nav-tree-project-data',
    isExpanded: true,
    isExpandable: true,
    isExpandedOnSelect: true,
    permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_CASE_DATA })],
    navigationInfo: new NavigationInfo(
        { name: 'All Docs', path: 'document-list', default: true },
        { name: 'Email Threads', path: 'email-thread' },
        { name: 'Short Message Threads', path: 'short-message-thread' },
        { name: 'Communication Grid', path: 'communication-matrix' },
        { name: 'Domain Grid', path: 'domain-matrix' },
        { name: 'Reports', path: 'reports' },
        { name: 'Clusters', path: 'cluster-list' }),
    showContextMenuButton: true,
    contextMenuOptions: [
        new ContextMenuOption({
            name: 'Add Tags ...', iconClass: 'fa-tag', iconColor: 'color-grey-05', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_TAGS, permissionActions: [ActionType.CREATE] })]
        }),
        new ContextMenuOption({
            name: 'Remove Tags ...', iconClass: 'fa-tag', iconColor: 'color-grey-05', iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_TAGS, permissionActions: [ActionType.CREATE] })]
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Add to ...', iconClass: 'fa-folder', iconColor: 'color-yellow-05', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_FOLDER, permissionActions: [ActionType.CREATE] }),
            { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({
            name: 'Remove from ...', iconClass: 'fa-folder', iconColor: 'color-yellow-05', iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_FOLDER, permissionActions: [ActionType.CREATE] }),
            { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Find Exact Duplicates', iconClass: 'fa-files-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-search', iconSecondColor: 'color-yellow-07',
            enabled: (item: GenericTreeNode) => item.customNumber > 0
        }),
        new ContextMenuOption({
            name: 'Find Content Duplicates', iconClass: 'fa-files-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-search', iconSecondColor: 'color-yellow-07',
            enabled: (item: GenericTreeNode) => item.customNumber > 0
        }),
        new ContextMenuOption({
            divider: true,
            visible: (item: GenericTreeNode) => (item.data instanceof WorkingSet)
        }),
        new ContextMenuOption({
            name: 'Import External Images ...',
            visible: (item: GenericTreeNode) => (item.data instanceof WorkingSet),
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] }),
            { permissionType: ObjectActionListType.CONNECTOR_ACCESS }],
        }),
        new ContextMenuOption({ divider: true }),
        // enable/disable for word list options is handled before context menu is opened(in generic grid and generic tree) because can have different values on sequential context menu openings
        new ContextMenuOption({
            name: 'Calculate Word List', iconClass: 'fa-font', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-refresh', iconSecondColor: 'color-blue-01',
        }),
        new ContextMenuOption({
            name: 'View Word List ...', iconClass: 'fa-font', iconColor: 'color-blue-01', tooltip: 'Please Calculate Word List to view it'
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Create Manifest ...', iconClass: 'fa-list-alt', iconColor: 'color-blue-01',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.DOCUMENT_REPORTS })]
        }),
        new ContextMenuOption({
            name: 'Create Comparison ...',
            iconClass: 'fa-exchange', iconColor: 'color-purple-02',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.CREATE] })]
        }),
        new ContextMenuOption({
            name: 'Create Sample ...', iconClass: 'fa-eyedropper', iconColor: 'color-tan-07',
            enabled: (item: GenericTreeNode) => item.customNumber > 0
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Download All as PDFs', iconClass: 'fa-file-pdf-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_PDF }]
        }),
        new ContextMenuOption({
            name: 'Download All Natives', iconClass: 'fa-file', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_NATIVES }]
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Export to Review ...',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            visible: (item: GenericTreeNode) => item.entityType === EntityType.tagView,
            permissions: [{ permissionType: ObjectActionListType.TLS_EXPORT_TO_VIEW }]
        }),
        new ContextMenuOption({ divider: true, visible: (item: GenericTreeNode) => item.entityType === EntityType.tagView }),
        new ContextMenuOption({
            name: 'Add Family',
            enabled: (item: GenericTreeNode) => item.customNumber > 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] })]
        }),
        new ContextMenuOption({ divider: true, visible: (item: GenericTreeNode) => !(item.data instanceof WorkingSet) && !(item.data instanceof View) }),
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
            visible: (item: GenericTreeNode) => (!(item.data instanceof WorkingSet) && !(item.data instanceof View)) || (item.entityType === 'tagView'),
            enabled: (item: GenericTreeNode) => (!(item.data instanceof Custodian && item.data.name === 'Unassigned') && !(item.data instanceof MediaID && item.data.name === 'Unassigned') && !(item.data instanceof Batch && item.data.name === 'Unassigned')),
            permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
            visible: (item: GenericTreeNode) => (!(item.data instanceof WorkingSet) && !(item.data instanceof View)) || (item.entityType === 'tagView'),
            enabled: (item: GenericTreeNode) => (!(item.data instanceof Custodian && item.data.name === 'Unassigned') && !(item.data instanceof MediaID && item.data.name === 'Unassigned') && !(item.data instanceof Batch && item.data.name === 'Unassigned')),
            permissions: [new PermissionSet({ permissionActions: [ActionType.DELETE] })]
        })
    ]
});

export const ProjectDataCustodianMenu = new GenericTreeNode({
    name: 'Custodians',
    entityType: EntityType.custodian,
    automationId: 'nav-tree-project-data-custodians',
    isExpandable: true,
    isExpandedOnSelect: true,
    childPadding: 0,
    parent: ProjectDataMenu,
    permissions: [{ permissionType: ObjectActionListType.ECA_CUSTODIAN }],
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.ecaManager_listCustodians,
        responseName: 'custodians',
        data: {}
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new Custodian(data);
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.custodian,
            childPadding: 7.5,
            isExpandable: false, // true if has samples
            hasExpanderPadding: true,
            customNumber: item.documentCount,
            icon: item.name === 'Unassigned' ? 'fa-user-circle' : 'fa-user-circle-o',
            iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_CUSTODIAN })],
            contextMenuOptions: ProjectDataMenu.contextMenuOptions,
            onContextMenuAction: ProjectDataMenu.onContextMenuAction,
            navigationInfo: ProjectDataMenu.navigationInfo,
            data: item,
            automationId: 'project-data-custodians-' + item.name
        }, handler);
    },

    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Custodian ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [{ permissionType: ObjectActionListType.ECA_CUSTODIAN, permissionActions: [ActionType.CREATE] }]
        })
    ]
});

export const ProjectDataMediaIDsMenu = new GenericTreeNode({
    name: 'MediaIDs',
    entityType: EntityType.mediaID,
    automationId: 'nav-tree-project-data-mediaids',
    isExpandable: true,
    isExpandedOnSelect: true,
    childPadding: 0,
    parent: ProjectDataMenu,
    permissions: [{ permissionType: ObjectActionListType.ECA_MEDIAID }],
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.ecaManager_listMediaids,
        responseName: 'mediaids',
        data: {
            includeUnassignedMetadataView: true
        }
    }),
    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new MediaID(data);
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.mediaID,
            childPadding: 7.5,
            isExpandable: false,
            hasExpanderPadding: true,
            customNumber: item.documentCount,
            icon: 'fa-laptop',
            iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_MEDIAID })],
            contextMenuOptions: ProjectDataMenu.contextMenuOptions,
            onContextMenuAction: ProjectDataMenu.onContextMenuAction,
            navigationInfo: ProjectDataMenu.navigationInfo,
            data: item,
            automationId: 'project-data-mediaids-' + item.name
        }, handler);
    },
    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New MediaID ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [{ permissionType: ObjectActionListType.ECA_MEDIAID, permissionActions: [ActionType.CREATE] }]
        })
    ]
});

export const ProjectDataBatchesMenu = new GenericTreeNode({
    name: 'Batches',
    entityType: EntityType.batch,
    automationId: 'nav-tree-project-data-batches',
    isExpandable: true,
    isExpandedOnSelect: true,
    childPadding: 0,
    parent: ProjectDataMenu,
    permissions: [{ permissionType: ObjectActionListType.ECA_BATCH }],
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.ecaManager_listBatches,
        responseName: 'batches',
        data: {
            includeUnassignedMetadataView: true
        }
    }),
    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new Batch(data);
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.batch,
            childPadding: 7.5,
            isExpandable: false,
            hasExpanderPadding: true,
            customNumber: item.documentCount,
            icon: 'fa-archive',
            iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_BATCH })],
            contextMenuOptions: ProjectDataMenu.contextMenuOptions,
            onContextMenuAction: ProjectDataMenu.onContextMenuAction,
            navigationInfo: ProjectDataMenu.navigationInfo,
            data: item,
            automationId: 'project-data-batches-' + item.name
        }, handler);
    },
    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Batch ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [{ permissionType: ObjectActionListType.ECA_BATCH, permissionActions: [ActionType.CREATE] }]
        })
    ]
});

export const ProjectDataTagMenu = new GenericTreeNode({
    name: 'Tags',
    entityType: EntityType.tagView,
    automationId: 'nav-tree-project-data-tags',
    isExpandable: true,
    isExpandedOnSelect: true,
    parent: ProjectDataMenu,
    childPadding: 0,
    permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_TAGS })],
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.workingSetManager_listWorkingSetViews,
        responseName: 'views',
        data: { filters: [{ attribute: 'viewType', value: 'TAG', operator: 'EQUALS' }] },
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new View(data);
        let colorValue = null;
        if (item.attributes && item.attributes.length > 0) {
            const colorAttrib = item.attributes.find(a => a.name === 'displayValue');
            if (colorAttrib) {
                colorValue = colorAttrib.value;
            }
        }
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.tagView,
            childPadding: 7.5,
            isExpandable: false, // true if has samples
            hasExpanderPadding: true,
            customNumber: item.documentCount,
            icon: 'fa-tag',
            iconColor: colorValue,
            contextMenuOptions: ProjectDataMenu.contextMenuOptions,
            onContextMenuAction: ProjectDataMenu.onContextMenuAction,
            navigationInfo: ProjectDataMenu.navigationInfo,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_TAGS })],
            data: item,
            automationId: 'project-data-tag-' + item.name
        }, handler);
    },

    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Tag ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04',
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_TAGS, permissionActions: [ActionType.CREATE] })]
        })
    ]
});

export const ProjectDataFolderMenu = new GenericTreeNode({
    name: 'Folders',
    entityType: EntityType.folder,
    automationId: 'nav-tree-project-data-folders',
    isExpandable: true,
    isExpandedOnSelect: true,
    childPadding: 0,
    parent: ProjectDataMenu,
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_FOLDER })],
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.projectManager_listDynamicFolders,
        responseName: 'dynamicFolders',
        data: {}
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new DynamicFolder(data);
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.folder,
            childPadding: 7.5,
            isExpandable: false, // true if has samples
            hasExpanderPadding: true,
            customNumber: item.documentCount,
            icon: 'fa-folder',
            iconColor: item.color,
            contextMenuOptions: ProjectDataMenu.contextMenuOptions,
            onContextMenuAction: ProjectDataMenu.onContextMenuAction,
            navigationInfo: ProjectDataMenu.navigationInfo,
            data: item,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_FOLDER })],
            automationId: 'project-data-folder-' + item.name
        }, handler);
    },

    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Folder ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04',
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ECA_FOLDER, permissionActions: [ActionType.CREATE] })]
        })
    ]
});

ProjectDataMenu.children = [
    ProjectDataCustodianMenu,
    ProjectDataMediaIDsMenu,
    ProjectDataBatchesMenu,
    ProjectDataTagMenu,
    ProjectDataFolderMenu
];
